import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type NewsPromotionType = 'NEWS' | 'PROMOTION';

export interface NewsPromotionDto {
  id: number;
  type: NewsPromotionType;
  title: string;
  summary: string;
  content: string;
  publishedDate: string;
  image: string[] | string | number[] | null;
}

interface NewsPromotionApiResponse {
  success: boolean;
  message: string;
  pagination: {
    pages: number;
    perPage: number;
    currentPage: number;
    totalObjects: number;
  };
  newsPromotion: NewsPromotionDto | null;
  newsPromotions: NewsPromotionDto[];
}

export interface NewsPromotionItem {
  id: number;
  type: NewsPromotionType;
  title: string;
  summary: string;
  content: string;
  publishedDate: string;
  imageUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class NewsPromotionService {
  private apiUrl = environment.baseHref + 'api/v1/';
  private cache: NewsPromotionItem[] = [];

  constructor(private http: HttpClient) {}

  getAll(page: number = 1, pageSize: number = 10): Observable<{ message: string; items: NewsPromotionItem[] }> {
    return this.http
      .get<NewsPromotionApiResponse>(`${this.apiUrl}news-promotion-service/all`, {
        params: {
          page: String(page),
          pageSize: String(pageSize)
        }
      })
      .pipe(
        map((response: NewsPromotionApiResponse) => {
          const dtos = response?.newsPromotions || [];
          const items = dtos.map((dto) => this.mapDto(dto));
          return {
            message: response?.message || 'No records found',
            items
          };
        }),
        tap((data) => {
          this.cache = data.items;
        })
      );
  }

  getById(id: number): Observable<NewsPromotionItem | null> {
    const cached = this.cache.find((item) => item.id === id) || null;
    if (cached) {
      return of(cached);
    }

    return this.getAll(1, 100).pipe(
      map((data) => data.items.find((item) => item.id === id) || null)
    );
  }

  private mapDto(dto: NewsPromotionDto): NewsPromotionItem {
    return {
      id: dto.id,
      type: dto.type,
      title: dto.title,
      summary: dto.summary,
      content: dto.content,
      publishedDate: dto.publishedDate,
      imageUrl: this.toImageUrl(dto.image)
    };
  }

  private toImageUrl(image: string[] | string | number[] | null): string {
    if (!image) {
      return 'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1400&q=80';
    }

    if (typeof image === 'string') {
      if (image.startsWith('data:image')) return image;
      return `data:image/jpeg;base64,${image}`;
    }

    if (Array.isArray(image) && image.length > 0) {
      if (typeof image[0] === 'string') {
        const raw = (image as string[]).join('');
        if (raw.startsWith('data:image')) return raw;
        return `data:image/jpeg;base64,${raw}`;
      }

      const binary = String.fromCharCode(...(image as number[]));
      return `data:image/jpeg;base64,${btoa(binary)}`;
    }

    return 'https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1400&q=80';
  }
}
